import React from "react";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";

import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

import Langs, { Localize } from "../../../app/lang/langs";
import {
    VLearnProgressBar, VLearnTypeIcon,
    formatVLearnDuration, vlearnSurface, vlearnType
} from "../../../components/vlearn/vlearnUi";

/**
 * V-Learning — the dashboard's per-lesson statistics, as a card list for a phone.
 *
 * `LessonStatsTable` puts seven columns in a `minWidth: 880` table inside an
 * `overflowX: 'auto'` container. That is correct on a desktop and correct about not widening
 * the document — but on a 360px screen the six numeric columns are entirely off-screen, and
 * they are the whole content: the visible part is the lesson title and nothing else. Worse,
 * the "needs attention" flag that this table exists to surface is in the title cell while the
 * completion rate justifying it is 500px to the right, so the finding and its evidence can
 * never be read together.
 *
 * A card keeps them together: the title, its flag, the completion bar and the six figures as
 * labelled pairs. Same rows, same server fields, same ordering (weakest first) — only the
 * arrangement differs, and nothing is recomputed here either.
 *
 * Presentational: `LessonStatsTable` owns the selector, the sort and the threshold and hands
 * the finished rows down. See MobileLearnerProgressList for why this lives under
 * `containers/vlearn/mobile/` despite holding no state.
 *
 * @param {Array}  rows               lesson rows, already sorted weakest-first
 * @param {number} attentionThreshold the completion rate below which a required lesson is flagged
 * @param {string} lang
 */
export default function MobileLessonStatsList({ rows, attentionThreshold, lang }) {

    const list = rows || []

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {list.map((lesson) => {
                const rate = Number(lesson.completion_rate) || 0
                const mandatory = !!Number(lesson.is_mandatory)
                // Optional lessons are never flagged: "below the threshold" is a finding about
                // a lesson the course required, and an optional one carries no such
                // expectation to fall short of. Same rule as the table.
                const needsAttention = rate < attentionThreshold && mandatory
                const averageTime = formatVLearnDuration(lesson.average_seconds)
                const typeLabel = Localize(
                    `VLEARN_TYPE_${String(lesson.type || 'text').toUpperCase()}`, lang)

                return (
                    <Box
                        key={lesson.id}
                        sx={{
                            ...vlearnSurface.card,
                            padding: '14px',
                            // The tint AND the worded chip below, never the tint alone: this
                            // is the one thing the panel exists to say, and saying it in
                            // colour only says it to some readers.
                            backgroundColor: needsAttention ? 'rgba(251,105,98,0.05)' : '#fff',
                            borderColor: needsAttention
                                ? 'rgba(251,105,98,0.28)' : 'rgba(0,0,0,0.08)'
                        }}
                    >
                        {/* ── Which lesson ───────────────────────────────────────── */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                            {/* The desktop hangs the type name on a Tooltip, which does not
                                exist on touch — so the icon is labelled instead. */}
                            <Box sx={{ display: 'flex', pt: '2px', flexShrink: 0 }}
                                role="img" aria-label={typeLabel}>
                                <VLearnTypeIcon type={lesson.type} />
                            </Box>

                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography sx={{ ...vlearnType.cardTitle }}>
                                    {lesson.title}
                                </Typography>
                                <Box sx={{
                                    display: 'flex', alignItems: 'center', gap: 0.75,
                                    flexWrap: 'wrap', mt: 0.4
                                }}>
                                    <Chip
                                        size="small"
                                        label={<Langs str={mandatory
                                            ? "VLEARN_LESSON_MANDATORY" : "VLEARN_OPTIONAL"} />}
                                        sx={{
                                            height: 18, fontSize: 10,
                                            backgroundColor: mandatory
                                                ? 'rgba(0,40,128,0.08)' : 'rgba(0,0,0,0.05)',
                                            color: mandatory
                                                ? 'var(--vital-blue, #002880)' : 'rgba(0,0,0,0.55)'
                                        }}
                                    />
                                    <Typography sx={{ ...vlearnType.meta }}>
                                        {lesson.section_title || typeLabel}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        {needsAttention ? (
                            <Box sx={{
                                display: 'inline-flex', alignItems: 'center', gap: 0.4, mt: 1
                            }}>
                                <WarningAmberRoundedIcon
                                    sx={{ fontSize: 14, color: '#B3271F' }} aria-hidden="true" />
                                <Typography sx={{
                                    fontSize: '0.72rem', fontWeight: 700, color: '#B3271F'
                                }}>
                                    <Langs str="VLEARN_LESSON_NEEDS_ATTENTION"
                                        params={{ threshold: attentionThreshold }} />
                                </Typography>
                            </Box>
                        ) : null}

                        {/* ── Completion, which is what the sort is on ────────────── */}
                        <Box sx={{ mt: 1.25 }}>
                            <Box sx={{
                                display: 'flex', alignItems: 'center',
                                justifyContent: 'space-between', mb: 0.5
                            }}>
                                <Typography sx={{
                                    ...vlearnType.meta, textTransform: 'uppercase',
                                    letterSpacing: '.04em', fontWeight: 600, fontSize: '0.66rem'
                                }}>
                                    <Langs str="VLEARN_COL_COMPLETION_RATE" />
                                </Typography>
                                <Typography sx={{
                                    fontSize: '0.82rem', fontWeight: 700,
                                    fontVariantNumeric: 'tabular-nums',
                                    color: 'var(--text-primary, #111318)'
                                }}>
                                    {`${Math.round(rate)}%`}
                                </Typography>
                            </Box>
                            <VLearnProgressBar percent={rate} />
                        </Box>

                        {/* ── The five remaining figures ─────────────────────────── */}
                        <Box sx={{
                            mt: 1.25, pt: 1.25, borderTop: '1px solid rgba(0,0,0,0.06)',
                            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px'
                        }}>
                            <Figure labelKey="VLEARN_COL_UNIQUE_VIEWERS"
                                value={Number(lesson.unique_viewers) || 0} />
                            <Figure labelKey="VLEARN_COL_TOTAL_VIEWS"
                                value={Number(lesson.total_views) || 0} />
                            <Figure labelKey="VLEARN_COL_COMPLETIONS"
                                value={Number(lesson.completions) || 0} />
                            <Figure labelKey="VLEARN_COL_AVERAGE_TIME"
                                value={averageTime || '—'} />
                            <Figure labelKey="VLEARN_COL_DOWNLOADS"
                                value={Number(lesson.downloads) || 0} />
                        </Box>
                    </Box>
                )
            })}
        </Box>
    )
}

/** One labelled figure in the card's detail grid. */
function Figure({ labelKey, value }) {
    return (
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{
                ...vlearnType.meta, textTransform: 'uppercase',
                letterSpacing: '.04em', fontWeight: 600, fontSize: '0.66rem'
            }}>
                <Langs str={labelKey} />
            </Typography>
            <Typography sx={{
                fontSize: '0.82rem', fontWeight: 600,
                fontVariantNumeric: 'tabular-nums',
                color: 'var(--text-primary, #111318)'
            }}>
                {value}
            </Typography>
        </Box>
    )
}
